import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";
import useMediaQ from "@root/hooks/useMediaQ";

/**
 * Tour Guide for Pledge Update Profile page.
 * Excludes hidden fields (Organization Type, Company, Department, Role)
 * which are only shown to JOURNALIST users.
 */
export const usePledgeUpdateProfileTourGuide = () => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();
  const { sm } = useMediaQ();
  const isMobile = !sm;

  const isOnPledgeUpdateProfile =
    pathname === ROUTE_NAMES.PLEDGE_UPDATE_PROFILE;

  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "pledgeUpdateProfile",
      enabled: isOnPledgeUpdateProfile,
      steps: [
        {
          id: "pledge-update-profile-change-history",
          targetSelector:
            '[data-tour-item="journalist-update-profile-change-history"]',
          title: t("tourGuide.pledgeUpdateProfile.step1.title"),
          description: t("tourGuide.pledgeUpdateProfile.step1.description"),
          position: "bottom",
          offset: { x: 0, y: 12 },
          highlightPadding: 10,
        },
        {
          id: "pledge-update-profile-avatar",
          targetSelector: '[data-tour-item="journalist-update-profile-avatar"]',
          title: t("tourGuide.pledgeUpdateProfile.step2.title"),
          description: t("tourGuide.pledgeUpdateProfile.step2.description"),
          position: "bottom",
          offset: { x: 0, y: 12 },
          highlightPadding: 12,
        },
        {
          id: "pledge-update-profile-name",
          targetSelector: '[data-tour-item="journalist-update-profile-name"]',
          title: t("tourGuide.pledgeUpdateProfile.step3.title"),
          description: t("tourGuide.pledgeUpdateProfile.step3.description"),
          position: "right",
          offset: { x: 12, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "pledge-update-profile-email",
          targetSelector: '[data-tour-item="journalist-update-profile-email"]',
          title: t("tourGuide.pledgeUpdateProfile.step4.title"),
          description: t("tourGuide.pledgeUpdateProfile.step4.description"),
          position: "right",
          offset: { x: 12, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "pledge-update-profile-phone",
          targetSelector: '[data-tour-item="journalist-update-profile-phone"]',
          title: t("tourGuide.pledgeUpdateProfile.step5.title"),
          description: t("tourGuide.pledgeUpdateProfile.step5.description"),
          position: "right",
          offset: { x: 12, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "pledge-update-profile-change-password",
          targetSelector:
            '[data-tour-item="journalist-update-profile-change-password"]',
          title: t("tourGuide.pledgeUpdateProfile.step6.title"),
          description: t("tourGuide.pledgeUpdateProfile.step6.description"),
          position: "top",
          offset: { x: 0, y: -12 },
          highlightPadding: 10,
        },
        {
          id: "pledge-update-profile-save",
          targetSelector: '[data-tour-item="journalist-update-profile-save"]',
          title: t("tourGuide.pledgeUpdateProfile.step7.title"),
          description: t("tourGuide.pledgeUpdateProfile.step7.description"),
          position: "top",
          offset: { x: 0, y: -12 },
          highlightPadding: 10,
        },
      ],
    }),
    [isOnPledgeUpdateProfile, t],
  );

  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [
            { type: TourGuideType.PledgeUpdateProfile, isDone: true },
          ],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const pledgeUpdateProfileTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.PledgeUpdateProfile,
    isOnTourPath: isOnPledgeUpdateProfile && !isMobile,
    onTourComplete: handleTourComplete,
  });

  useEffect(() => {
    if (pledgeUpdateProfileTourGuide.isActive) {
      setActiveTourModule("pledgeUpdateProfile");
      setCurrentTourStep(pledgeUpdateProfileTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    pledgeUpdateProfileTourGuide.isActive,
    pledgeUpdateProfileTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  return {
    pledgeUpdateProfileTourGuide,
    isMobile,
  };
};
